import { useMemo, useState } from "react";
import { groupBy, cloneDeep } from "es-toolkit/compat";

import { groupSalesDataByMakeModel } from "@/migrated-widgets/table-map/utils/grouping";
import { SalesData } from "@/migrated-widgets/table-map/services/MapDataLayer/types/Transactions.types";
import { Transaction } from "../types";

export const useTransactionMarkers = (_transactions: SalesData[]) => {
    // TagMakers filters
    const [vehicleTypes, setVehicleTypes] = useState<string[]>([]);
    const [makes, setMakes] = useState<string[]>([]);
    const [models, setModels] = useState<string[]>([]);
    const [zipCodes, setZipCodes] = useState<string[]>([]);
    const [dealers, setDealers] = useState<string[]>([]);

    const salesMarkers = useMemo(() => {
        const transactions = _transactions.filter((transaction) => transaction.transactionType === "S");
        let filteredTransactions = dealers.length > 0 ? transactions.filter((transaction) => !dealers.includes(transaction.clientName)) : transactions;
        if (models.length > 0) {
            filteredTransactions = filteredTransactions.filter((transaction) => transaction.vehicleType === "NEW" && models.includes(transaction.modelName));
        } else if (makes.length > 0) {
            filteredTransactions = filteredTransactions.filter((transaction) => transaction.vehicleType === "NEW" && makes.includes(transaction.makeName));
        } else if (vehicleTypes.length > 0) {
            filteredTransactions = filteredTransactions.filter((transaction) => vehicleTypes.includes(transaction.vehicleType));
        }

        const groupByZipCode = groupBy(filteredTransactions, (transaction) => transaction.zipcode);

        const _transactionsMarkers: Transaction[] = Object.values(groupByZipCode).map((_transactions) => {
            const newSales = groupSalesDataByMakeModel(_transactions, "NEW");
            const usedSales = groupSalesDataByMakeModel(_transactions, "USED");
            const newSold = newSales.reduce((acc, crr) => acc + crr.totalSold, 0);
            const usedSold = usedSales.reduce((acc, crr) => acc + crr.totalSold, 0);
            const txWithCoords = _transactions.find((t) => t.latitude && t.longitude);
            return {
                newSales,
                usedSales,
                newSold,
                usedSold,
                transactionType: _transactions[0].transactionType || "S",
                totalSold: _transactions.reduce((acc, t) => acc + t.totalSold, 0),
                zipCode: _transactions[0].zipcode,
                position: txWithCoords ? { lat: txWithCoords.latitude, lng: txWithCoords.longitude } : undefined,
            };
        }).filter((m) => !!(m.position?.lat && m.position?.lng));
        return cloneDeep(_transactionsMarkers);
    }, [_transactions, vehicleTypes, dealers, makes, models]);



    const repairOrdersMarkers = useMemo(() => {
        const transactions = _transactions.filter((transaction) => transaction.transactionType === "R");
        let filteredTransactions = dealers.length > 0 ? transactions.filter((transaction) => !dealers.includes(transaction.clientName)) : transactions;
        if (models.length > 0) {
            filteredTransactions = filteredTransactions.filter((transaction) => transaction.vehicleType === "NEW" && models.includes(transaction.modelName));
        } else if (makes.length > 0) {
            filteredTransactions = filteredTransactions.filter((transaction) => transaction.vehicleType === "NEW" && makes.includes(transaction.makeName));
        } else if (vehicleTypes.length > 0) {
            filteredTransactions = filteredTransactions.filter((transaction) => vehicleTypes.includes(transaction.vehicleType));
        }

        const groupByZipCode = groupBy(filteredTransactions, (transaction) => transaction.zipcode);

        const _transactionsMarkers: Transaction[] = Object.values(groupByZipCode).map((_transactions) => {
            const newSales = groupSalesDataByMakeModel(_transactions, "NEW");
            const usedSales = groupSalesDataByMakeModel(_transactions, "USED");
            const newSold = newSales.reduce((acc, crr) => acc + crr.totalSold, 0);
            const usedSold = usedSales.reduce((acc, crr) => acc + crr.totalSold, 0);
            const txWithCoords = _transactions.find((t) => t.latitude && t.longitude);
            return {
                newSales,
                usedSales,
                newSold,
                usedSold,
                transactionType: _transactions[0].transactionType || "S",
                totalSold: _transactions.reduce((acc, t) => acc + t.totalSold, 0),
                zipCode: _transactions[0].zipcode,
                position: txWithCoords ? { lat: txWithCoords.latitude, lng: txWithCoords.longitude } : undefined,
            };
        }).filter((m) => !!(m.position?.lat && m.position?.lng));
        return cloneDeep(_transactionsMarkers);
    }, [_transactions, vehicleTypes, dealers, makes, models]);


    const leadsMarkers = useMemo(() => {
        const transactions = _transactions.filter((transaction) => transaction.transactionType === "L");

        let filteredTransactions = dealers.length > 0 ? transactions.filter((transaction) => !dealers.includes(transaction.clientName)) : transactions;
        if (models.length > 0) {
            filteredTransactions = filteredTransactions.filter((transaction) => transaction.vehicleType === "NEW" && models.includes(transaction.modelName));
        } else if (makes.length > 0) {
            filteredTransactions = filteredTransactions.filter((transaction) => transaction.vehicleType === "NEW" && makes.includes(transaction.makeName));
        } else if (vehicleTypes.length > 0) {
            filteredTransactions = filteredTransactions.filter((transaction) => vehicleTypes.includes(transaction.vehicleType));
        }

        const groupByZipCode = groupBy(filteredTransactions, (transaction) => transaction.zipcode);

        const _transactionsMarkers: Transaction[] = Object.values(groupByZipCode).map((_transactions) => {
            const newSales = groupSalesDataByMakeModel(_transactions, "NEW");
            const usedSales = groupSalesDataByMakeModel(_transactions, "USED");
            const newSold = newSales.reduce((acc, crr) => acc + crr.totalSold, 0);
            const usedSold = usedSales.reduce((acc, crr) => acc + crr.totalSold, 0);
            const txWithCoords = _transactions.find((t) => t.latitude && t.longitude);
            return {
                newSales,
                usedSales,
                newSold,
                usedSold,
                transactionType: _transactions[0].transactionType || "S",
                totalSold: _transactions.reduce((acc, t) => acc + t.totalSold, 0),
                zipCode: _transactions[0].zipcode,
                position: txWithCoords ? { lat: txWithCoords.latitude, lng: txWithCoords.longitude } : undefined,
            };
        }).filter((m) => !!(m.position?.lat && m.position?.lng));
        return cloneDeep(_transactionsMarkers);
    }, [_transactions, vehicleTypes, dealers, makes, models]);

    return {
        dealers,
        zipCodes,
        vehicleTypes,
        makes,
        models,

        setDealers,
        setZipCodes,
        setVehicleTypes,
        setMakes,
        setModels,

        salesMarkers,
        repairOrdersMarkers,
        leadsMarkers,
    };
};
